import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, ArrowLeft, Heart, Users, Clock } from 'lucide-react';
import { siteInfo } from '../data/mockData';

const Funeral = () => {
  // Schema.org para SEO
  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Serviços Funerários",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Santa Casa Serviço Funerário Imediato",
      "image": "https://funerariasantacasaassistencial.com/logo.png",
      "telephone": "+55-21-4106-4022",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Meier",
        "addressLocality": "Rio de Janeiro",
        "addressRegion": "RJ",
        "addressCountry": "BR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-22.9035",
        "longitude": "-43.2096"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "00:00",
        "closes": "23:59"
      },
      "priceRange": "R$ 1.599 - R$ 4.599",
      "areaServed": ["Meier", "Tijuca", "Centro", "Barra da Tijuca", "Botafogo", "Rio de Janeiro"]
    },
    "description": "Serviços funerários completos no Rio de Janeiro com atendimento 24 horas. Funeral básico, completo e premium com valores transparentes.",
    "offers": [
      {
        "@type": "Offer",
        "name": "Funeral Básico",
        "price": "1599.00",
        "priceCurrency": "BRL"
      },
      {
        "@type": "Offer",
        "name": "Funeral Completo",
        "price": "2899.00",
        "priceCurrency": "BRL"
      },
      {
        "@type": "Offer",
        "name": "Funeral Premium",
        "price": "4599.00",
        "priceCurrency": "BRL"
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Quanto custa um funeral completo no Rio de Janeiro?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Os valores variam de R$ 1.599 (Funeral Básico) até R$ 4.599 (Funeral Premium). O Funeral Completo, nosso mais escolhido, custa R$ 2.899 e inclui todos os serviços essenciais."
        }
      },
      {
        "@type": "Question",
        "name": "Quais serviços estão incluídos no funeral?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Nossos serviços incluem: caixão, transporte, velório, ornamentação, higienização, tanatopraxia, necromaquiagem, vestimenta, véu, manto, flores, documentação e agendamento de sepultamento ou cremação."
        }
      },
      {
        "@type": "Question",
        "name": "A funerária atende 24 horas?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim! Nosso atendimento é 24 horas por dia, 7 dias por semana, incluindo finais de semana e feriados. Entre em contato pelo WhatsApp (21) 4106-4022 a qualquer momento."
        }
      },
      {
        "@type": "Question",
        "name": "Quais regiões do Rio de Janeiro são atendidas?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Atendemos todo o Rio de Janeiro, com especial presença no Meier, Tijuca, Centro, Barra da Tijuca, Botafogo e toda a região metropolitana."
        }
      },
      {
        "@type": "Question",
        "name": "Como funciona o pagamento dos serviços funerários?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Aceitamos pagamento por cartão de crédito ou PIX. Oferecemos valores transparentes e orçamento detalhado sem compromisso."
        }
      }
    ]
  };

  const servicos = [
    {
      title: 'Velório e Cerimonial',
      description: 'Organizamos todo o velório com estrutura completa, flores, iluminação adequada e ambiente respeitoso para a despedida.'
    },
    {
      title: 'Documentação Completa',
      description: 'Cuidamos de toda a documentação necessária: certidão de óbito, licenças, autorizações e registros.'
    },
    {
      title: 'Preparação e Tanatopraxia',
      description: 'Realizamos a preparação do corpo com técnicas profissionais, garantindo dignidade e respeito.'
    },
    {
      title: 'Urnas e Esquifes',
      description: 'Oferecemos diversas opções de urnas e esquifes, desde os mais simples até os mais sofisticados.'
    },
    {
      title: 'Transporte Funerário',
      description: 'Veículos apropriados para translado do corpo, realizados com todo cuidado e discrição.'
    },
    {
      title: 'Flores e Coroas',
      description: 'Arranjos florais personalizados e coroas de flores para homenagear o ente querido.'
    },
    {
      title: 'Cerimônia Religiosa',
      description: 'Coordenamos cerimônias religiosas respeitando todas as crenças e tradições da família.'
    },
    {
      title: 'Apoio Familiar',
      description: 'Suporte emocional e assistência integral à família em todos os momentos do processo.'
    }
  ];

  const diferenciais = [
    'Atendimento 24 horas no Meier',
    'Equipe especializada e humanizada',
    'Estrutura completa para velório',
    'Todas as opções de sepultamento',
    'Documentação agilizada',
    'Respeito a todas as religiões',
    'Transparência nos valores',
    'Suporte pós-funeral'
  ];

  return (
    <div className="min-h-screen">
      {/* Schema.org JSON-LD para SEO */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrg)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(26, 188, 156, 0.85), rgba(20, 160, 133, 0.85)), url('https://images.unsplash.com/photo-1604585571723-8915f3b61275?w=1920&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="text-center px-4 max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Serviços Funerários Completos</h1>
          <p className="text-xl md:text-2xl text-teal-50">Organização completa de funeral no Meier com respeito e dignidade</p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-lime-600 hover:text-teal-600 transition-colors">
              Home
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">Funeral</span>
          </div>
        </div>
      </div>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Organização Completa de Funeral no Rio de Janeiro</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              A Santa Casa Serviço Funerário Imediato oferece serviços completos para organização de funeral, cuidando de cada detalhe 
              para que sua família possa se concentrar no que realmente importa: honrar e despedir-se do ente querido 
              com dignidade e respeito.
            </p>
          </div>

          {/* Seção de Valores e Transparência */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-2 text-center">Tabela de Preços: Quanto Custa um Funeral Completo?</h3>
            <p className="text-center text-gray-600 mb-12">Valores atualizados 2025 • Inclui todos os serviços</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 max-w-6xl mx-auto">
              {/* Card 1: Funeral Básico */}
              <div className="bg-gradient-to-br from-teal-50 to-green-50 border-2 border-teal-400 rounded-2xl p-8 hover:shadow-xl transition-all">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">🪦</div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Funeral Básico</h4>
                  <p className="text-4xl font-bold text-teal-600 mb-2">R$ 1.599</p>
                  <p className="text-sm text-gray-600">Funeral completo com todos os serviços básicos</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Caixão padrão</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Transporte do local de falecimento para cemitério</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Ornamentação com flores sobre caixão</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Higienização</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Vestimenta</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Véu</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Manto</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Agendamento de sepultamento ou cremação</span>
                  </div>
                </div>

                <div className="bg-teal-100 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-700 text-center">Menor custo-benefício<br/>Funeral completo com dignidade</p>
                </div>

                <a
                  href="https://wa.me/552141064022?text=Olá,%20gostaria%20de%20um%20orçamento%20para%20Funeral%20Básico"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: 'package_select',
                      package_name: 'Funeral Básico',
                      package_value: '1599'
                    });
                    window.dataLayer.push({
                      event: 'whatsapp_click',
                      click_location: 'funeral_page',
                      package: 'Funeral Básico'
                    });
                  }}
                  className="block w-full bg-gradient-to-r from-teal-400 to-green-400 hover:from-teal-500 hover:to-green-500 text-white py-3 rounded-lg font-bold text-center transition-all"
                >
                  Pagamento por cartão ou PIX
                </a>
              </div>

              {/* Card 2: Funeral Completo - DESTAQUE */}
              <div className="bg-gradient-to-br from-teal-50 to-green-50 border-4 border-teal-500 rounded-2xl p-8 hover:shadow-2xl transition-all relative transform md:scale-105">
                <div className="absolute -top-3 right-4">
                  <span className="bg-red-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase shadow-lg">
                    Mais Escolhido
                  </span>
                </div>
                
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">🪦</div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Funeral Completo</h4>
                  <p className="text-4xl font-bold text-teal-600 mb-2">R$ 2.899</p>
                  <p className="text-sm text-gray-600">Sepultamento tradicional completo</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Caixão com visor</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Transporte do local de falecimento</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Transporte para o cemitério</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Ornamentação com flores</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Serviços de laboratório</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Higienização</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Tanatopraxia</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Necromaquiagem</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Vestimenta</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Véu</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Manto</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Uma coroa de flores</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Serviço destacante (cartório)</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Agendamento de sepultamento ou cremação</span>
                  </div>
                </div>

                <a
                  href="https://wa.me/552141064022?text=Olá,%20gostaria%20de%20um%20orçamento%20para%20Funeral%20Completo"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: 'package_select',
                      package_name: 'Funeral Completo',
                      package_value: '2899'
                    });
                    window.dataLayer.push({
                      event: 'whatsapp_click',
                      click_location: 'funeral_page',
                      package: 'Funeral Completo'
                    });
                  }}
                  className="block w-full bg-gradient-to-r from-teal-400 to-green-400 hover:from-teal-500 hover:to-green-500 text-white py-3 rounded-lg font-bold text-center transition-all"
                >
                  Pagamento por cartão ou PIX
                </a>
              </div>

              {/* Card 3: Funeral Premium */}
              <div className="bg-gradient-to-br from-teal-50 to-green-50 border-2 border-teal-400 rounded-2xl p-8 hover:shadow-xl transition-all">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">👑</div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Funeral Premium</h4>
                  <p className="text-4xl font-bold text-teal-600 mb-2">R$ 4.599</p>
                  <p className="text-sm text-gray-600">Máximo conforto e dignidade</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Caixão com visor</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Transporte do local de falecimento</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Transporte para o cemitério</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Ornamentação com flores</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Serviços de laboratório</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Higienização</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Tanatopraxia</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Necromaquiagem</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Vestimenta</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Véu</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Manto</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Uma coroa de flores (M)</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Serviço destacante (cartório)</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Agendamento de sepultamento ou cremação</span>
                  </div>
                </div>

                <a
                  href="https://wa.me/552141064022?text=Olá,%20gostaria%20de%20um%20orçamento%20para%20Funeral%20Premium"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: 'package_select',
                      package_name: 'Funeral Premium',
                      package_value: '4599'
                    });
                    window.dataLayer.push({
                      event: 'whatsapp_click',
                      click_location: 'funeral_page',
                      package: 'Funeral Premium'
                    });
                  }}
                  className="block w-full bg-gradient-to-r from-teal-400 to-green-400 hover:from-teal-500 hover:to-green-500 text-white py-3 rounded-lg font-bold text-center transition-all"
                >
                  Pagamento por cartão ou PIX
                </a>
              </div>
            </div>

            {/* Texto Orçamentos Personalizados */}
            <div className="text-center mb-8">
              <p className="text-gray-700">
                <strong>Não encontrou o que procura?</strong> Fazemos orçamentos personalizados.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Atendimento Imediato</h3>
              <p className="text-gray-700">Disponíveis 24 horas para atender você no momento em que mais precisa</p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Atendimento Humanizado</h3>
              <p className="text-gray-700">Equipe treinada para oferecer suporte emocional e acolhimento</p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Experiência e Tradição</h3>
              <p className="text-gray-700">Mais de 18.000 famílias atendidas no Meier e região</p>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Nossos Serviços Funerários</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicos.map((servico, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{servico.title}</h3>
                <p className="text-gray-600 text-sm">{servico.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Como Funciona</h2>
          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Primeiro Contato</h3>
                <p className="text-gray-700 leading-relaxed">
                  Entre em contato conosco por telefone ou WhatsApp. Nossa equipe está disponível 24 horas para atendê-lo 
                  imediatamente, oferecendo orientação e suporte desde o primeiro momento.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Planejamento Personalizado</h3>
                <p className="text-gray-700 leading-relaxed">
                  Conversamos com a família para entender desejos e necessidades. Apresentamos opções de serviços, 
                  urnas, flores e local do velório, respeitando o orçamento e as preferências familiares.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Documentação</h3>
                <p className="text-gray-700 leading-relaxed">
                  Cuidamos de toda a burocracia: emissão de certidão de óbito, autorizações necessárias, 
                  liberação do corpo e todos os trâmites legais com agilidade e eficiência.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                4
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Velório e Cerimônia</h3>
                <p className="text-gray-700 leading-relaxed">
                  Organizamos todo o velório: preparação do ambiente, ornamentação com flores, coordenação da cerimônia 
                  religiosa (se desejado) e todo o suporte necessário durante o período.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                5
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Sepultamento ou Cremação</h3>
                <p className="text-gray-700 leading-relaxed">
                  Realizamos o cortejo fúnebre, acompanhamos a família até o cemitério ou crematório, 
                  coordenamos toda a cerimônia final com respeito e dignidade.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                6
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Suporte Pós-Funeral</h3>
                <p className="text-gray-700 leading-relaxed">
                  Continuamos oferecendo suporte após o funeral: auxílio com documentação de benefícios, 
                  orientações sobre o luto e apoio sempre que a família precisar.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Por Que Escolher a Santa Casa Serviço Funerário Imediato?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {diferenciais.map((diferencial, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white rounded-lg p-4 shadow">
                <CheckCircle className="w-5 h-5 text-lime-600 flex-shrink-0 mt-1" />
                <span className="text-gray-800">{diferencial}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-lime-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Precisa de Atendimento Imediato?</h2>
          <p className="text-xl mb-8 text-teal-50">
            Nossa equipe está disponível 24 horas para oferecer todo o suporte que você precisa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'phone_click',
                  click_location: 'funeral_page_cta',
                  phone_number: siteInfo.phone
                });
              }}
              className="inline-flex items-center justify-center space-x-2 bg-white text-lime-600 hover:bg-gray-100 px-8 py-4 rounded-md text-lg font-medium transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5" />
              <span>Ligar Agora</span>
            </a>
            <Link
              to="/contato"
              className="inline-block bg-gray-900 text-white hover:bg-gray-800 px-8 py-4 rounded-md text-lg font-medium transition-all duration-300"
            >
              Formulário de Contato
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content Expandido */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Funerária no Meier - Organização Completa de Funeral
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              A <strong>Santa Casa Serviço Funerário Imediato</strong> é especializada em <strong>organização de funeral no Meier</strong> e em toda 
              a região do Rio de Janeiro. Oferecemos <strong>serviços funerários completos</strong> incluindo velório, sepultamento, 
              cremação e translado, sempre com respeito e dignidade.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Nossa <strong>funerária no Meier</strong> conta com estrutura completa para realização de velórios, equipe 
              especializada disponível 24 horas e toda a assistência necessária para organização de <strong>funeral no Rio de Janeiro</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Se você procura por <strong>funerária no Meier RJ</strong> com atendimento humanizado e serviços completos, 
              conte com a Santa Casa Serviço Funerário Imediato. Atendemos famílias há anos com excelência em <strong>serviços funerários</strong>.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Por que escolher nossa funerária no Meier?</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Com mais de 18.000 famílias atendidas, somos referência em <strong>serviços funerários no Rio de Janeiro</strong>. 
              Nossa localização estratégica no <strong>Meier</strong> permite atender rapidamente toda a região, incluindo 
              <strong> Tijuca, Centro, Barra da Tijuca e Botafogo</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Oferecemos <strong>três planos de funeral</strong> com valores transparentes: <strong>Funeral Básico a partir de R$ 1.599</strong>, 
              <strong> Funeral Completo por R$ 2.899</strong> (nosso mais escolhido) e <strong>Funeral Premium por R$ 4.599</strong>. 
              Todos incluem documentação completa, transporte, velório e agendamento de sepultamento ou cremação.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Serviços funerários 24 horas no Rio de Janeiro</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Sabemos que a perda de um ente querido pode acontecer a qualquer momento. Por isso, nossa <strong>funerária funciona 24 horas</strong>, 
              todos os dias da semana, incluindo finais de semana e feriados. Nossa equipe está sempre pronta para oferecer 
              <strong> atendimento imediato e humanizado</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Além do <strong>Meier</strong>, atendemos com a mesma excelência em toda a <strong>Zona Norte, Zona Sul, Centro e Barra</strong> do Rio de Janeiro. 
              Nossos profissionais são treinados para cuidar de cada detalhe do funeral, permitindo que você e sua família possam 
              se concentrar na despedida do ente querido.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Valores transparentes e sem surpresas</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Trabalhamos com <strong>total transparência nos valores</strong>. Todos os nossos planos incluem os serviços essenciais, 
              e você recebe um <strong>orçamento detalhado</strong> antes de contratar. Aceitamos pagamento por <strong>cartão de crédito ou PIX</strong>, 
              facilitando o processo em um momento já difícil.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Nossa <strong>funerária no Meier</strong> é reconhecida pela qualidade dos serviços, atendimento respeitoso e valores justos. 
              Entre em contato pelo WhatsApp <strong>(21) 4106-4022</strong> e receba atendimento imediato.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Perguntas Frequentes sobre Serviços Funerários</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quanto custa um funeral completo no Rio de Janeiro?</h3>
              <p className="text-gray-700">
                Os valores variam de R$ 1.599 (Funeral Básico) até R$ 4.599 (Funeral Premium). O Funeral Completo, 
                nosso mais escolhido, custa R$ 2.899 e inclui todos os serviços essenciais: caixão com visor, transporte, 
                velório, ornamentação, tanatopraxia, necromaquiagem e documentação completa.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quais serviços estão incluídos no funeral?</h3>
              <p className="text-gray-700">
                Nossos serviços incluem: caixão (padrão ou com visor), transporte do local de falecimento e para o cemitério, 
                velório completo, ornamentação com flores, higienização, tanatopraxia, necromaquiagem, vestimenta, véu, manto, 
                coroa de flores (planos completo e premium), serviços de laboratório, documentação e agendamento de sepultamento ou cremação.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">A funerária atende 24 horas?</h3>
              <p className="text-gray-700">
                Sim! Nosso atendimento é 24 horas por dia, 7 dias por semana, incluindo finais de semana e feriados. 
                Entre em contato pelo WhatsApp (21) 4106-4022 a qualquer momento e receba atendimento imediato de nossa equipe especializada.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quais regiões do Rio de Janeiro são atendidas?</h3>
              <p className="text-gray-700">
                Atendemos todo o Rio de Janeiro, com especial presença no Meier (nossa sede), Tijuca, Centro, Barra da Tijuca, 
                Botafogo e toda a região metropolitana. Oferecemos remoção e translado para qualquer região do estado.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Como funciona o pagamento dos serviços funerários?</h3>
              <p className="text-gray-700">
                Aceitamos pagamento por cartão de crédito ou PIX. Oferecemos valores transparentes e você recebe um 
                orçamento detalhado sem compromisso antes de contratar qualquer serviço. Não trabalhamos com cobranças ocultas.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Qual a diferença entre os planos Básico, Completo e Premium?</h3>
              <p className="text-gray-700">
                O <strong>Funeral Básico (R$ 1.599)</strong> inclui os serviços essenciais com caixão padrão. 
                O <strong>Funeral Completo (R$ 2.899)</strong>, nosso mais escolhido, adiciona caixão com visor, tanatopraxia completa, 
                necromaquiagem, uma coroa de flores e serviços de laboratório. 
                O <strong>Funeral Premium (R$ 4.599)</strong> oferece o máximo conforto e dignidade com todos os serviços inclusos 
                e coroa de flores tamanho M.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">O que é tanatopraxia e por que é importante?</h3>
              <p className="text-gray-700">
                A tanatopraxia é o processo de conservação e preparação do corpo, incluindo higienização, tratamento químico 
                e reconstituição quando necessário. É importante para garantir uma apresentação digna durante o velório, 
                permitindo que familiares e amigos possam se despedir adequadamente.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fazem cremação no Rio de Janeiro?</h3>
              <p className="text-gray-700">
                Sim! Oferecemos serviços completos de cremação em crematórios certificados do Rio de Janeiro. 
                O serviço inclui velório, transporte ao crematório, taxa de cremação, entrega da urna com cinzas e 
                documentação completa. Entre em contato para orçamento específico de cremação.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mapa Google - Localização */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Nossa Localização no Meier</h2>
          <p className="text-center text-gray-600 mb-8">
            Funerária 24 horas no Meier, Rio de Janeiro - Fácil acesso de toda a região
          </p>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3675.1987654321!2d-43.27789!3d-22.90456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDU0JzE2LjQiUyA0M8KwMTYnNDAuNCJX!5e0!3m2!1spt-BR!2sbr!4v1234567890"
              width="100%" 
              height="450" 
              style={{ border: 0 }}
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização Santa Casa Serviço Funerário Imediato - Meier, Rio de Janeiro"
            ></iframe>
          </div>
          <div className="mt-6 text-center">
            <p className="text-gray-700 mb-2">
              <strong>Atendemos 24 horas:</strong> Meier, Tijuca, Centro, Barra, Botafogo e todo o Rio de Janeiro
            </p>
            <a
              href="https://wa.me/552141064022?text=Olá,%20gostaria%20de%20informações%20sobre%20localização"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-bold transition-all mt-4"
            >
              Entrar em Contato pelo WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Back button */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <Link
          to="/"
          className="inline-flex items-center text-lime-600 hover:text-teal-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para início
        </Link>
      </div>
    </div>
  );
};

export default Funeral;